"use client";

import { Provider } from "react-redux";
import { SessionProvider } from "next-auth/react";
import { useEffect, useState } from "react";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import cartReducer from "./cartSlice";
import themeReducer, { initializeTheme } from "./SliceTheme";
import authReducer from "./authSlice";
import orderReducer from "./orderSlice";
import Loader from "@/app/components/loader/loader";
import { PersistGate } from "redux-persist/integration/react";
import {
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

const createNoopStorage = () => ({
  getItem: async () => null,
  setItem: async (_key, value) => value,
  removeItem: async () => undefined,
});

const storage =
  typeof window === "undefined"
    ? createNoopStorage()
    : createWebStorage("local");

const rootReducer = combineReducers({
  theme: themeReducer,
  auth: authReducer,
  cart: cartReducer,
  order: orderReducer,
});

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "cart", "theme"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

function createStore() {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });

  return store;
}

export function ReduxProvider({ children }) {
  const [store, setStore] = useState(null);
  const [persistor, setPersistor] = useState(null);

  useEffect(() => {
    const newStore = createStore();
    const newPersistor = persistStore(newStore, undefined, () => {
      newStore.dispatch(initializeTheme());
    });
    setStore(newStore);
    setPersistor(newPersistor);
  }, []);

  if (!store || !persistor) return <Loader />;

  return (
    <SessionProvider>
      <Provider store={store}>
        <PersistGate loading={<Loader />} persistor={persistor}>
          {children}
        </PersistGate>
      </Provider>
    </SessionProvider>
  );
}
